export type PlayerLike = {
  playerId: number | string;
  firstName?: string | null;
  lastName?: string | null;
};

export type NormalizedNameParts = {
  first: string;
  middle: string;
  last: string;
  full: string;
};

export type PlayerMatchScore = {
  score: number;
  normalized: NormalizedNameParts;
};

export function normalize(str: string | null | undefined): string {
  return (str ?? '').toLowerCase().replace(/[()]/g, ' ').replace(/\s+/g, ' ').trim();
}

export function splitFirstAndMiddle(firstNameRaw: string | null | undefined): { first: string; middle: string } {
  const raw = firstNameRaw ?? '';
  const match = raw.match(/^(.*?)\s*\((.*?)\)\s*$/);

  if (match) {
    return {
      first: normalize(match[1]),
      middle: normalize(match[2]),
    };
  }

  return {
    first: normalize(raw),
    middle: '',
  };
}

function isWordStart(text: string, token: string): boolean {
  return text.startsWith(token) || text.includes(` ${token}`);
}

function isWholeWord(text: string, token: string): boolean {
  if (!token) return false;
  if (text === token) return true;

  if (text.startsWith(token) && (text.length === token.length || text[token.length] === ' ')) {
    return true;
  }

  if (text.includes(` ${token} `) || text.endsWith(` ${token}`)) {
    return true;
  }

  return false;
}

export function scorePlayerMatch(player: PlayerLike, queryRaw: string): PlayerMatchScore | null {
  const raw = queryRaw ?? '';

  const rawHasSpace = /\s/.test(raw);
  const endsWithSpace = /\s$/.test(raw);

  const q = normalize(raw);
  if (!q) return null;

  const tokens: string[] = q.split(' ').filter(Boolean);

  const { first, middle } = splitFirstAndMiddle(player.firstName);
  const last = normalize(player.lastName);
  const full = normalize(`${player.firstName ?? ''} ${player.lastName ?? ''}`);
  const id = (player.playerId ?? '').toString();

  const boundaryMatch = (text: string, t: string) => text.startsWith(t) || text.includes(` ${t}`);

  // --- MATCH ---
  const isIdMatch = id.includes(q);

  const tokenMatch =
    tokens.length > 0 &&
    tokens.every((t, idx) => {
      const isLastToken = idx === tokens.length - 1;

      if (tokens.length >= 2 && idx === 0) {
        return isWordStart(first, t);
      }

      if (endsWithSpace && isLastToken) return boundaryMatch(full, t);

      if (rawHasSpace) return boundaryMatch(full, t);

      return full.includes(t);
    });

  if (!isIdMatch && !tokenMatch) return null;

  // --- SCORE ---

  let score = 0;

  for (let i = 0; i < tokens.length; i++) {
    const t = tokens[i];
    const isFirstToken = i === 0;

    const singleToken = tokens.length === 1;
    const wMiddle = singleToken ? 0 : isFirstToken ? 180 : 220;
    const wLast = singleToken ? 0 : isFirstToken ? 120 : 320;

    const wFirst = isFirstToken ? 400 : 80;

    const preferWholeWord = endsWithSpace && i === tokens.length - 1;

    if (preferWholeWord) {
      if (isWholeWord(first, t)) score += 1200;
      if (isWholeWord(middle, t)) score += 800;
      if (isWholeWord(last, t)) score += 700;
    }

    // FIRST
    if (first.startsWith(t)) score += wFirst;
    else if (isWordStart(first, t)) score += Math.floor(wFirst * 0.7);
    else if (!rawHasSpace && first.includes(t)) score += Math.floor(wFirst * 0.45);

    // LAST
    if (wLast > 0) {
      if (last.startsWith(t)) score += wLast;
      else if (isWordStart(last, t)) score += Math.floor(wLast * 0.7);
      else if (!rawHasSpace && last.includes(t)) score += Math.floor(wLast * 0.45);
    }

    // MIDDLE
    if (wMiddle > 0) {
      if (middle.startsWith(t)) score += wMiddle;
      else if (isWordStart(middle, t)) score += Math.floor(wMiddle * 0.7);
      else if (!rawHasSpace && middle.includes(t)) score += Math.floor(wMiddle * 0.45);
    }

    if (rawHasSpace && !isFirstToken && last.startsWith(t)) score += 100;

    if (singleToken) {
      const firstHit = isWordStart(first, t) || (!rawHasSpace && first.includes(t));

      const middleHit = isWordStart(middle, t) || (!rawHasSpace && middle.includes(t));

      const lastHit = isWordStart(last, t) || (!rawHasSpace && last.includes(t));

      if (rawHasSpace && !firstHit && (middleHit || lastHit)) {
        score -= 1200;
      } else if (!firstHit && (middleHit || lastHit)) {
        score -= 350;
      }
    }
  }

  if (full.startsWith(q)) score += 30;

  if (isIdMatch) score += 20;

  return {
    score,
    normalized: { first, middle, last, full },
  };
}
